/* eslint-disable react/prop-types */
/*
  Job Profile Score drawer — appears as a right-side sheet inside Editor.
  - Pick a saved profile OR describe inline
  - Run AI score: 0-100 with matching skills, missing skills, weak areas, ATS gaps, suggestions
  - One-click "Apply AI improvements" rewrites the resume to raise the score
*/
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Target, CheckCircle2, XCircle, AlertTriangle, Sparkles, Save, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function JobScoreDrawer({ open, onOpenChange, resumeId, onDataPatched }) {
  const [profiles, setProfiles] = useState([]);
  const [sel, setSel] = useState("__inline");
  const [inline, setInline] = useState({
    title: "", summary: "", responsibilities: "",
    required_skills: "", preferred_skills: "",
    experience_min_years: "", experience_max_years: "", industry: "",
  });
  const [scoring, setScoring] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [saveName, setSaveName] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/job-profiles").then((r) => setProfiles(r.data || [])).catch(() => {});
  }, [open]);

  const buildPayload = () => {
    if (sel !== "__inline" && sel) return { job_profile_id: sel };
    return {
      inline_profile: {
        title: inline.title, summary: inline.summary, responsibilities: inline.responsibilities,
        required_skills: (inline.required_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        preferred_skills: (inline.preferred_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        experience_min_years: inline.experience_min_years ? Number(inline.experience_min_years) : null,
        experience_max_years: inline.experience_max_years ? Number(inline.experience_max_years) : null,
        industry: inline.industry,
      },
    };
  };

  const score = async (apply = false) => {
    const setter = apply ? setApplying : setScoring;
    setter(true);
    try {
      const payload = { ...buildPayload(), apply_improvements: apply };
      const r = await api.post(`/resumes/${resumeId}/score`, payload);
      setResult(r.data.score_result);
      if (apply && r.data.applied && r.data.data) {
        onDataPatched?.(r.data.data);
        toast.success(`Resume updated — projected score: ${r.data.score_result.score}+`);
      } else if (!apply) {
        toast.success(`Score: ${r.data.score_result.score}/100`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Score failed"); }
    finally { setter(false); }
  };

  const saveProfile = async () => {
    if (!saveName.trim()) return toast.error("Give the profile a title first");
    try {
      const body = {
        title: saveName,
        summary: inline.summary, responsibilities: inline.responsibilities,
        required_skills: (inline.required_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        preferred_skills: (inline.preferred_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        experience_min_years: inline.experience_min_years ? Number(inline.experience_min_years) : 0,
        experience_max_years: inline.experience_max_years ? Number(inline.experience_max_years) : 20,
        industry: inline.industry,
      };
      const r = await api.post("/job-profiles", body);
      toast.success("Profile saved");
      setProfiles([r.data, ...profiles]);
      setSel(r.data.id);
      setSaveName("");
    } catch { toast.error("Save failed"); }
  };

  const deleteProfile = async (pid) => {
    if (!window.confirm("Delete this job profile?")) return;
    await api.delete(`/job-profiles/${pid}`);
    setProfiles(profiles.filter((x) => x.id !== pid));
    if (sel === pid) setSel("__inline");
  };

  const scoreColor = result && (result.score >= 80 ? "text-emerald-500" : result.score >= 60 ? "text-sky-500" : result.score >= 40 ? "text-amber-500" : "text-rose-500");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[580px] overflow-y-auto" data-testid="job-score-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Target className="size-5" /> Job Profile Score</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold">Job profile</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger data-testid="score-profile-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__inline">✏️ Describe inline</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.title || "(untitled)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {sel === "__inline" && (
            <Card className="border-border">
              <CardContent className="p-3 space-y-2">
                <Input placeholder="Job title (e.g. Senior Electrical Engineer)" value={inline.title} onChange={(e) => setInline({ ...inline, title: e.target.value })} data-testid="score-title" />
                <Textarea rows={2} placeholder="Role summary" value={inline.summary} onChange={(e) => setInline({ ...inline, summary: e.target.value })} data-testid="score-summary" />
                <Textarea rows={3} placeholder="Key responsibilities" value={inline.responsibilities} onChange={(e) => setInline({ ...inline, responsibilities: e.target.value })} />
                <Input placeholder="Required skills (comma-separated)" value={inline.required_skills} onChange={(e) => setInline({ ...inline, required_skills: e.target.value })} data-testid="score-required" />
                <Input placeholder="Preferred skills (comma-separated)" value={inline.preferred_skills} onChange={(e) => setInline({ ...inline, preferred_skills: e.target.value })} />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Min years" value={inline.experience_min_years} onChange={(e) => setInline({ ...inline, experience_min_years: e.target.value })} />
                  <Input placeholder="Max years" value={inline.experience_max_years} onChange={(e) => setInline({ ...inline, experience_max_years: e.target.value })} />
                  <Input placeholder="Industry" value={inline.industry} onChange={(e) => setInline({ ...inline, industry: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Input placeholder="Save this as… (profile name)" value={saveName} onChange={(e) => setSaveName(e.target.value)} className="h-8 text-xs" />
                  <Button size="sm" variant="outline" onClick={saveProfile} className="gap-1 h-8" data-testid="score-save-profile"><Save className="size-3.5" /> Save</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {sel !== "__inline" && profiles.find((p) => p.id === sel) && (
            <Card className="border-border">
              <CardContent className="p-3 text-xs">
                {(() => {
                  const p = profiles.find((x) => x.id === sel);
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-sm display">{p.title}</div>
                        <Button size="icon" variant="ghost" onClick={() => deleteProfile(p.id)} data-testid={`score-delete-${p.id}`} className="text-destructive h-7 w-7"><Trash2 className="size-3.5" /></Button>
                      </div>
                      <div className="text-muted-foreground mt-1">{p.industry} · {p.experience_min_years}-{p.experience_max_years} yrs</div>
                      {p.summary && <div className="mt-2">{p.summary}</div>}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(p.required_skills || []).map((s, i) => <span key={i} className="chip">{s}</span>)}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          <Button onClick={() => score(false)} disabled={scoring || applying} className="w-full gap-2 lift" data-testid="score-run">
            {scoring ? <Loader2 className="size-4 animate-spin" /> : <Target className="size-4" />} Calculate Match Score
          </Button>

          {result && (
            <div className="space-y-3" data-testid="score-result">
              <div className="text-center py-4 rounded-lg border border-border">
                <div className={`text-5xl font-black display ${scoreColor}`} data-testid="score-value">{result.score}<span className="text-2xl text-muted-foreground">/100</span></div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold mt-1">Match vs {result.job_title || "Job profile"}</div>
                {result.role_fit_summary && <div className="text-sm mt-3 px-4 leading-relaxed">{result.role_fit_summary}</div>}
              </div>

              {result.matching_skills?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-bold flex items-center gap-1.5 mb-1.5"><CheckCircle2 className="size-3.5" /> Matching skills ({result.matching_skills.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {result.matching_skills.map((s, i) => <span key={i} className="chip bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">{s}</span>)}
                  </div>
                </div>
              )}

              {result.missing_skills?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-rose-600 font-bold flex items-center gap-1.5 mb-1.5"><XCircle className="size-3.5" /> Missing skills ({result.missing_skills.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {result.missing_skills.map((s, i) => <span key={i} className="chip bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30">{s}</span>)}
                  </div>
                </div>
              )}

              {result.weak_areas?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-bold flex items-center gap-1.5 mb-1.5"><AlertTriangle className="size-3.5" /> Weak areas</div>
                  <ul className="text-sm space-y-0.5 text-muted-foreground">
                    {result.weak_areas.map((w, i) => <li key={i}>• {w}</li>)}
                  </ul>
                </div>
              )}

              {result.ats_keyword_gaps?.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-sky-600 font-bold mb-1.5">ATS keyword gaps</div>
                  <div className="flex flex-wrap gap-1">
                    {result.ats_keyword_gaps.map((s, i) => <span key={i} className="chip bg-sky-500/10 text-sky-700 dark:text-sky-300">{s}</span>)}
                  </div>
                </div>
              )}

              {result.improvement_suggestions?.length > 0 && (
                <Card className="border-primary/40">
                  <CardContent className="p-3">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-bold mb-1.5 flex items-center gap-1.5"><Sparkles className="size-3.5" /> AI improvement suggestions</div>
                    <ul className="text-sm space-y-1">
                      {result.improvement_suggestions.map((s, i) => <li key={i}>✨ {s}</li>)}
                    </ul>
                    <Button onClick={() => score(true)} disabled={applying || scoring} className="w-full mt-3 gap-2" data-testid="score-apply">
                      {applying ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />} Apply AI Improvements
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
